"use client";

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, SlidersHorizontal, ShieldCheck, Users, GitBranch, ListPlus, Receipt, Plug } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import PageTabs from "@/components/layout/PageTabs";
import { can, getCurrentOrganization } from "@/utils/service";
import General from "./General";
import Roles from "./Roles";
import Teams from "./Teams";
import Pipelines from "./Pipelines";
import CustomFields from "./CustomFields";
import InvoiceSettings from "./InvoiceSettings";
import Integrations from "./Integrations";

/** Organization administration, laid out like personal Settings: section tabs over one panel. */
export default function OrganizationPage() {
  const [params, setParams] = useSearchParams();
  const items = [
    { id: "company", label: "Company & Branding", icon: Building2, show: can("organization.view") },
    { id: "sales", label: "Numbering & Rules", icon: SlidersHorizontal, show: can("organization.view") },
    { id: "roles", label: "Roles & Permissions", icon: ShieldCheck, show: can("roles.view") },
    { id: "teams", label: "Teams", icon: Users, show: can("teams.view", "members.view") },
    { id: "pipelines", label: "Pipelines", icon: GitBranch, show: can("settings.view", "deals.view") },
    { id: "fields", label: "Custom Fields", icon: ListPlus, show: can("settings.view", "settings.manage") },
    { id: "invoices", label: "Invoices", icon: Receipt, show: can("organization.view", "organization.update", "invoices.view", "invoices.configure") },
    { id: "integrations", label: "Webhooks & Integrations", icon: Plug, show: can("integrations.view") },
  ].filter((i) => i.show);
  const requestedTab = params.get("tab") === "finance" ? "invoices" : params.get("tab");
  const [active, setActive] = useState(requestedTab && items.some((i) => i.id === requestedTab) ? requestedTab : items[0]?.id);

  const select = (id: string) => { setActive(id); setParams({ tab: id }, { replace: true }); };

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <PageHeader title="Organization" description={getCurrentOrganization()?.name ?? "Organization settings"} />
      <PageTabs tabs={items} active={active} onChange={select}>
        <div className="space-y-6">
          {active === "company" && <General section="company" />}
          {active === "sales" && <General section="sales" />}
          {active === "roles" && <Roles />}
          {active === "teams" && <Teams />}
          {active === "pipelines" && <Pipelines />}
          {active === "fields" && <CustomFields />}
          {active === "invoices" && <InvoiceSettings />}
          {active === "integrations" && <Integrations />}
        </div>
      </PageTabs>
    </div>
  );
}
